import { useEffect } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { isValidGuid } from '../../utils/storeUtils';

/**
 * StoreIdGuard component ensures that:
 * 1. StoreId is always present in the URL for all customer routes (including login/register)
 * 2. StoreId is stored in localStorage when accessed from URL path
 * 3. If storeId is missing from URL and not in localStorage, redirect to 404
 * 4. If storeId is missing from URL but exists in localStorage, redirect to correct path with storeId
 */
const StoreIdGuard = ({ children }) => {
  const { storeId: routeStoreId } = useParams();
  const location = useLocation();
  const storedStoreId = localStorage.getItem('currentStoreId');
  const isStoreRoute = location.pathname.startsWith('/stores/');
  const isValidRouteStoreId = !routeStoreId || isValidGuid(routeStoreId);

  useEffect(() => {
    if (!isStoreRoute) {
      // Not a store route, let it pass through
      return;
    }

    // If route has storeId, store it (update if different)
    if (routeStoreId && isValidGuid(routeStoreId)) {
      if (storedStoreId !== routeStoreId) {
        localStorage.setItem('currentStoreId', routeStoreId);
        localStorage.removeItem('currentStoreName');
        localStorage.removeItem('currentStoreSlug');
      }
      return;
    }
  }, [routeStoreId, storedStoreId, isStoreRoute]);

  // Block invalid storeId immediately so nested providers/pages don't fire API calls.
  if (isStoreRoute && !isValidRouteStoreId) {
    return <Navigate to="/not-found" replace />;
  }

  return children;
};

export default StoreIdGuard;

